<script setup lang="ts" generic="T extends string | number">
import type { HTMLAttributes } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'
import { ChevronDown, X } from 'lucide-vue-next'
import { cn } from '@/ui/lib/utils'

export interface ComboboxOption<V extends string | number = string | number> {
  value: V
  label: string
  disabled?: boolean
}

export interface ComboboxGroup<V extends string | number = string | number> {
  heading: string
  items: ComboboxOption<V>[]
}

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    options?: ComboboxOption<T>[] | Record<string, unknown>[]
    groups?: ComboboxGroup<T>[]
    placeholder?: string
    emptyMessage?: string
    searchPlaceholder?: string
    searchThreshold?: number
    disabled?: boolean
    disablePortal?: boolean
    clearable?: boolean
    hasMore?: boolean
    isLoadingMore?: boolean
    class?: HTMLAttributes['class']
    triggerClass?: HTMLAttributes['class']
    valueKey?: string
    labelKey?: string
  }>(),
  {
    options: () => [],
    groups: () => [],
    placeholder: '',
    searchThreshold: 6,
    disabled: false,
    disablePortal: false,
    clearable: false,
    hasMore: false,
    isLoadingMore: false,
    valueKey: 'value',
    labelKey: 'label'
  }
)

const emit = defineEmits<{
  loadMore: []
  search: [value: string]
}>()

const normalizedOptions = computed<(ComboboxOption<T> & Record<string, unknown>)[]>(() =>
  (props.options as Record<string, unknown>[]).map((o) => ({
    ...o,
    value: o[props.valueKey] as T,
    label: o[props.labelKey] as string,
    disabled: Boolean(o.disabled)
  }))
)

const resolvedEmptyMessage = computed(() => props.emptyMessage ?? t('global.words.noResult'))
const resolvedSearchPlaceholder = computed(() => props.searchPlaceholder ?? t('global.words.search'))

const modelValue = defineModel<T | null>({ default: null })

const open = ref(false)
const searchValue = ref('')
const commandListRef = ref<{ $el?: HTMLElement } | null>(null)
const panelElement = ref<HTMLElement | null>(null)

const allOptions = computed(() =>
  props.groups.length > 0 ? props.groups.flatMap((g) => g.items) : normalizedOptions.value
)

const showSearch = computed(() => props.hasMore || allOptions.value.length >= props.searchThreshold)

const selectedOption = computed(() =>
  modelValue.value !== null ? allOptions.value.find((o) => o.value === modelValue.value) : undefined
)

const triggerLabel = computed(() => selectedOption.value?.label ?? props.placeholder)

const showClear = computed(() => props.clearable && !props.disabled && modelValue.value !== null)

const selectOption = (option: ComboboxOption<T>) => {
  if (option.disabled) return
  modelValue.value = option.value
  open.value = false
}

const clear = () => {
  modelValue.value = null
  open.value = false
}

const onSearchInput = (value: unknown) => {
  searchValue.value = String(value ?? '')
  emit('search', searchValue.value)
}

useInfiniteScroll(
  panelElement,
  () => {
    if (props.hasMore && !props.isLoadingMore) {
      emit('loadMore')
    }
  },
  {
    distance: 10,
    canLoadMore: () => props.hasMore && !props.isLoadingMore
  }
)

watch(open, (isOpen) => {
  if (!isOpen) {
    if (searchValue.value !== '') {
      searchValue.value = ''
      emit('search', '')
    }
    return
  }

  if (props.hasMore) {
    nextTick(() => {
      panelElement.value = commandListRef.value?.$el ?? null
    })
  }
})
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child :disabled="disabled">
      <button
        type="button"
        role="combobox"
        :aria-expanded="open"
        :disabled="disabled"
        :class="
          cn(
            'flex h-10 w-full cursor-pointer items-center justify-between gap-2 rounded-control border border-input bg-background px-3 py-1 text-sm shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:bg-muted disabled:text-foreground-disabled',
            triggerClass
          )
        "
      >
        <slot name="trigger" :selected="selectedOption" :placeholder="placeholder">
          <span :class="['truncate text-left', !selectedOption && 'text-muted-foreground']">
            {{ triggerLabel }}
          </span>
        </slot>
        <span class="ml-auto flex shrink-0 items-center gap-1">
          <span
            v-if="showClear"
            role="button"
            tabindex="0"
            :aria-label="t('global.words.clear')"
            class="rounded-sm p-0.5 text-red-500 hover:bg-red-50 hover:text-red-600"
            @click.stop="clear"
            @keydown.enter.stop.prevent="clear"
            @keydown.space.stop.prevent="clear"
          >
            <X class="size-3.5" />
          </span>
          <ChevronDown class="size-4 text-muted-foreground" />
        </span>
      </button>
    </PopoverTrigger>

    <PopoverContent
      :class="cn('pointer-events-auto z-[1200] w-[var(--reka-popover-trigger-width)] p-0', props.class)"
      align="start"
      :disable-portal="disablePortal"
    >
      <Command :highlight-on-hover="true" :should-filter="!hasMore">
        <CommandInput v-if="showSearch" :placeholder="resolvedSearchPlaceholder" @update:model-value="onSearchInput" />
        <CommandList ref="commandListRef">
          <CommandEmpty class="!p-0">
            <slot name="empty" :search="searchValue">
              <div class="px-3 py-3 text-left text-sm text-muted-foreground">{{ resolvedEmptyMessage }}</div>
            </slot>
          </CommandEmpty>

          <template v-if="groups.length > 0">
            <CommandGroup v-for="group in groups" :key="group.heading" :heading="group.heading">
              <CommandItem
                v-for="option in group.items"
                :key="option.value"
                :value="option.label"
                :disabled="option.disabled"
                @select="selectOption(option)"
              >
                <slot name="option" :option="option" :selected="modelValue === option.value">
                  <span :class="modelValue === option.value ? 'font-semibold text-primary' : ''">
                    {{ option.label }}
                  </span>
                </slot>
              </CommandItem>
            </CommandGroup>
          </template>

          <CommandGroup v-else>
            <CommandItem
              v-for="option in normalizedOptions"
              :key="option.value"
              :value="option.label"
              :disabled="option.disabled"
              @select="selectOption(option)"
            >
              <slot name="option" :option="option" :selected="modelValue === option.value">
                <span :class="modelValue === option.value ? 'font-semibold text-primary' : ''">
                  {{ option.label }}
                </span>
              </slot>
            </CommandItem>
          </CommandGroup>

          <div v-if="isLoadingMore" class="flex justify-center px-3 py-2">
            <div class="h-4 w-4 animate-spin rounded-full border-2 border-border border-l-transparent" />
          </div>
        </CommandList>
      </Command>
    </PopoverContent>
  </Popover>
</template>
